/**
 * OpsMate 主页面
 * 从 AZ_AI_MVR_H5/src/pages/OpsMate/index.tsx 迁移
 * 替换 PageContainer → div, useModel → useOpsMateContext, Link → react-router-dom Link
 */
import {
  Button,
  Checkbox,
  ConfigProvider,
  Flex,
  Input,
  Modal,
  Spin,
  Typography,
  message,
} from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { XProvider, Bubble, ThoughtChain } from "@ant-design/x";
import XMarkdown from "@ant-design/x-markdown";
import { createStyles } from "antd-style";

import ContainerBg1 from "@/assets/opsmate/bg1.png";
import ContainerBg2 from "@/assets/opsmate/bg2.png";
import GbIcon from "@/assets/opsmate/gb.svg";

import { useAuth } from "@/context/auth";
import { useOpsMateContext } from "@/contexts/useOpsMateContext";
import { ConversationsMenus, CustomerSuggestion, DocummentList, Agreement } from "./components";
import { getUserAgreementUserid, postCreateUserAgreement } from "@/services/opsmate/agreement";
import { sessionAnswerReport } from "@/services/opsmate/sessionLog";
import { sanitizeBubbleListItems } from "@/hooks/opsmate/bubbleItems";

const useStyles = createStyles(() => ({
  container: {
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    backgroundPosition: "left top",
    paddingInlineStart: 18,
    borderRadius: 12,
    overflow: "hidden",
  },
  menuCollapsedBg: {
    backgroundImage: `url(${ContainerBg2})`,
  },
  menuNotCollapsedBg: {
    backgroundImage: `url(${ContainerBg1})`,
  },
  agreementContainer: {
    backgroundImage: `url(${ContainerBg2})`,
    padding: 16,
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    backgroundPosition: "left top",
  },
}));

function OpsMateInner() {
  const { styles } = useStyles();
  const { t } = useTranslation();

  const {
    pageHeight,
    docsCollapsed,
    collapsed: menuCollapsed,
    listRef,
    aiReasoning,
    setAIReasoning,
    aiReasoningVisible,
    setAIReasoningVisible,
    dislikeConfirmVisible,
    closeDislikeConfirm,
    confirmDislike,
    dislikeConfirmLoading,
    dislikeConfirmTarget,
    items,
    selectedKeys,
    roles,
    setItems,
    resetConversation,
    openSearch,
    setOpenSearch,
  } = useOpsMateContext();

  // 缓存 role 配置对象，避免每次渲染传新引用给 Bubble.List
  const roleConfig = useMemo(() => roles(setItems), [roles, setItems]);
  const bubbleItems = useMemo(() => sanitizeBubbleListItems(items), [items]);

  const { user: currentUser } = useAuth();

  const [hasRequested, setHasRequested] = useState(false);
  const [agreementData, setAgreementData] = useState<any>(null);

  // 点踩反馈弹窗表单状态
  const [dislikeReasons, setDislikeReasons] = useState<string[]>([]);
  const [dislikeOtherReason, setDislikeOtherReason] = useState("");

  const dislikeReasonOptions = [
    t("opsmate.dislikeReason1"),
    t("opsmate.dislikeReason2"),
    t("opsmate.dislikeReason3"),
    t("opsmate.dislikeReason4"),
    t("opsmate.dislikeReason5"),
    t("opsmate.dislikeReason6"),
  ];

  const dislikeReasonToFlag: Record<string, string> = {
    [t("opsmate.dislikeReason1")]: "AnswerIncorrectOrHasErrors",
    [t("opsmate.dislikeReason2")]: "NotRelevantToMyQuestion",
    [t("opsmate.dislikeReason3")]: "InformationIncompleteOrMissingKeyDetails",
    [t("opsmate.dislikeReason4")]: "ContentTooVagueOrNotActionable",
    [t("opsmate.dislikeReason5")]: "CitedSourcesNotAppropriate",
    [t("opsmate.dislikeReason6")]: "AnswerTookTooLongToGenerate",
  };

  const resetDislikeForm = () => {
    setDislikeReasons([]);
    setDislikeOtherReason("");
  };

  // ── 全局快捷键 (T7) ──
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      // Cmd/Ctrl + N → 新建对话
      if (mod && e.key === "n") {
        e.preventDefault();
        resetConversation?.();
      }
      // Cmd/Ctrl + K → 聚焦搜索
      if (mod && e.key === "k") {
        e.preventDefault();
        setOpenSearch(true);
      }
      // Escape → 关闭搜索
      if (e.key === "Escape" && openSearch) {
        e.preventDefault();
        setOpenSearch(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [openSearch, resetConversation, setOpenSearch]);

  // 获取是否同意协议
  const fetchAgreement = async (userId: string) => {
    try {
      const res = await getUserAgreementUserid(userId);
      setAgreementData(res.data);
    } catch (e) {
      console.error("getUserAgreementUserid failed", e);
    } finally {
      setHasRequested(true);
    }
  };

  useEffect(() => {
    const userId = currentUser?.id;
    if (userId) {
      fetchAgreement(String(userId));
    } else {
      setHasRequested(true);
    }
  }, [currentUser?.id]);

  // 同意协议
  const handleCreateAgreement = async () => {
    const userId = currentUser?.id;
    try {
      const res = await postCreateUserAgreement({
        UserId: String(userId),
        IsAgreement: true,
      });
      if (res) message.success(t("opsmate.termsAccepted"));
      fetchAgreement(String(userId));
    } catch (e) {
      console.error("postCreateUserAgreement failed", e);
      message.error(t("opsmate.termsAcceptFailed"));
    }
  };

  useEffect(() => {
    listRef?.current?.scrollTo({ top: "bottom", behavior: "smooth" });
  }, [items.length, listRef]);

  // AI 生成答案后，若最后一条消息的 summary 首次出现，则滚动到 Summary
  const lastAiSummaryRef = useRef<string>("");
  useEffect(() => {
    const lastAiItem = [...items].reverse().find((item) => item.role === "ai");
    const currentSummary = lastAiItem?.summary ?? "";
    const prevSummary = lastAiSummaryRef.current;
    if (!prevSummary && currentSummary && lastAiItem?.key) {
      requestAnimationFrame(() => {
        const el = document.getElementById(`ai-summary-${lastAiItem.key}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    }
    lastAiSummaryRef.current = currentSummary;
  }, [items]);

  // 正在获取是否同意协议
  if (!hasRequested) {
    return (
      <Spin spinning={false} description={t("opsmate.loadingAgreement")}>
        <div style={{ height: "calc(100vh - 80px)" }}></div>
      </Spin>
    );
  }

  // 未同意协议
  if (!agreementData?.IsAgreement) {
    return (
      <div style={{ padding: "20px 16px" }}>
        <Flex
          vertical
          style={{ height: "calc(100vh - 80px)" }}
          gap={10}
          className={styles.agreementContainer}
        >
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 0" }}>
            <Agreement />
          </div>
          <div style={{ textAlign: "center" }}>
            <ConfigProvider
              theme={{
                token: { borderRadius: 4 },
                components: { Button: { paddingInlineLG: 30 } },
              }}
            >
              <Button type="primary" size="large" onClick={handleCreateAgreement}>
                {t("opsmate.acknowledgeBtn")}
              </Button>
            </ConfigProvider>
          </div>
        </Flex>
      </div>
    );
  }
  const isNewAsk = items.length === 0 && selectedKeys === "add";

  // 显示正常页面
  return (
    <div
      style={{
        padding: "10px",
        height: "calc(100vh - 56px)",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <div>
        <XProvider
          theme={{
            token: { colorPrimary: "#830050", borderRadiusLG: 4 },
            components: { Conversations: { borderRadius: 4 } },
          }}
        >
          <Flex style={{ height: pageHeight, backgroundSize: "contain" }} gap={12}>
            <ConversationsMenus />
            <Flex
              className={[
                styles.container,
                menuCollapsed ? styles.menuCollapsedBg : styles.menuNotCollapsedBg,
              ].join(" ")}
              flex={1}
              style={{ minWidth: 0 }}
            >
              <Flex
                vertical
                style={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 0,
                  overflowY: "auto",
                  paddingRight: docsCollapsed ? 0 : 24,
                }}
                gap={8}
              >
                {/* 头信息 */}
                {/* <div style={{ marginTop: 15, position: "relative", minHeight: 32, zIndex: 10 }} /> */}

                {/* 对话列表 */}
                <div
                  style={{
                    flex: selectedKeys !== "add" ? 1 : "none",
                    minHeight: 0,
                    height: items.length > 0 ? "auto" : 0,
                    opacity: items.length > 0 ? 1 : 0,
                    transform: `translateY(${items.length > 0 ? "0" : "-20px"})`,
                    transition: "opacity 0.3s, transform 0.3s, flex 0.3s",
                    pointerEvents: items.length > 0 ? "auto" : "none",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      maxWidth: isNewAsk ? "56vw" : "80vw",
                      margin: "0 auto",
                      width: "100%",
                    }}
                  >
                    <Bubble.List
                      autoScroll
                      items={bubbleItems}
                      style={{ height: "100%", overflowY: "auto" }}
                      role={roleConfig}
                      ref={listRef}
                    />
                  </div>
                </div>

                {/* 发送框 */}
                <div
                  style={{
                    marginTop: isNewAsk ? "auto" : 0,
                    marginBottom: isNewAsk ? "auto" : 0,
                    transition: "margin 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
                  }}
                >
                  <CustomerSuggestion />
                </div>

                <div style={{ textAlign: "center", marginBottom: 10, lineHeight: 1 }}>
                  <Typography.Text
                    style={{ color: "rgba(102, 102, 102, 1)", fontSize: 12 }}
                    type="secondary"
                  >
                    {t("opsmate.disclaimer.part1")}
                    <span style={{ fontWeight: 600 }}>{t("opsmate.disclaimer.part2")}</span>
                    {t("opsmate.disclaimer.part3")}
                    <Link
                      to="/terms-of-use"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#830051", textDecoration: "underline" }}
                    >
                      {t("opsmate.disclaimer.termsOfUse")}
                    </Link>
                    {t("opsmate.disclaimer.part4")}
                  </Typography.Text>
                </div>
              </Flex>
              <DocummentList />
            </Flex>
          </Flex>
          <ThoughtChain />
        </XProvider>

        {/* AI Reasoning 弹窗 */}
        <Modal
          open={aiReasoningVisible}
          title={t("opsmate.aiReasoning")}
          footer={false}
          centered
          onCancel={() => setAIReasoningVisible(false)}
          width={670}
          styles={{ body: { height: 220, overflow: "auto" } }}
        >
          {aiReasoning && aiReasoning.trim() ? (
            <XMarkdown content={aiReasoning} />
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              本次回答暂无可展示的推理过程
            </Typography.Text>
          )}
        </Modal>

        {/* 点踩二次确认弹窗 */}
        <Modal
          open={!!dislikeConfirmVisible}
          onCancel={() => {
            resetDislikeForm();
            closeDislikeConfirm?.();
          }}
          centered
          width={670}
          title={null}
          footer={null}
          styles={{
            container: {
              padding: 0,
            },
            body: {
              width: 670,
              height: "auto",
              background: "#FFFFFF",
              boxShadow: "0px 0px 24px 8px rgba(77,0,47,0.15)",
              borderRadius: 8,
              overflow: "hidden",
              padding: 0,
            },
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", height: "auto" }}>
            {/* Header */}
            <div
              style={{
                padding: "0 16px 8px 16px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                background: "#f8f8f8",
                borderRadius: "8px  8px  0px  0px",
                marginBottom: 10,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <Typography.Text
                  style={{
                    display: "block",
                    marginTop: 14,
                    fontSize: 16,
                    fontWeight: 600,
                    color: "rgba(0,0,0,0.85)",
                  }}
                >
                  {t("opsmate.helpImprove")}
                </Typography.Text>
                <div style={{ marginTop: 10 }}>
                  <Typography.Text style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>
                    {t("opsmate.helpImproveDesc")}
                  </Typography.Text>
                </div>
              </div>
            </div>

            {/* Content */}
            <div style={{ flex: 1, padding: "0 16px 0 16px", overflow: "hidden" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 0 }}>
                {dislikeReasonOptions.map((label) => {
                  const checked = dislikeReasons.includes(label);
                  return (
                    <div
                      key={label}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setDislikeReasons((prev) =>
                          prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label],
                        );
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setDislikeReasons((prev) =>
                            prev.includes(label)
                              ? prev.filter((x) => x !== label)
                              : [...prev, label],
                          );
                        }
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        width: 638,
                        height: 40,
                        padding: "0 12px",
                        borderRadius: 4,
                        cursor: "pointer",
                        background: checked ? "rgba(131, 0, 81, 0.10)" : "transparent",
                        boxSizing: "border-box",
                      }}
                    >
                      <Checkbox
                        checked={checked}
                        onChange={(e) => {
                          const nextChecked = e.target.checked;
                          setDislikeReasons((prev) =>
                            nextChecked ? [...prev, label] : prev.filter((x) => x !== label),
                          );
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Typography.Text style={{ color: "rgba(0,0,0,0.85)" }}>
                        {label}
                      </Typography.Text>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 10 }}>
                <Typography.Text
                  style={{ display: "block", marginBottom: 8, color: "rgba(0,0,0,0.85)" }}
                >
                  {t("opsmate.otherReason")}
                </Typography.Text>
                <Input.TextArea
                  value={dislikeOtherReason}
                  onChange={(e) => setDislikeOtherReason(e.target.value)}
                  placeholder={t("opsmate.otherReasonPlaceholder")}
                  maxLength={1000}
                  showCount
                  style={{
                    width: "100%",
                    height: 100,
                    background: "#FFFFFF",
                    borderRadius: 8,
                    border: "1px solid #DDDDDD",
                    resize: "none",
                  }}
                />
              </div>
            </div>

            {/* Footer */}
            <div
              style={{
                marginTop: 10,
                padding: "0 16px 20px 16px",
                display: "flex",
                justifyContent: "flex-end",
                gap: 12,
                background: "#fff",
              }}
            >
              <Button
                onClick={() => {
                  resetDislikeForm();
                  closeDislikeConfirm?.();
                }}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="primary"
                loading={!!dislikeConfirmLoading}
                // disabled={dislikeReasons.length === 0 && !dislikeOtherReason.trim()}
                onClick={async () => {
                  const sessionLogId = (dislikeConfirmTarget as any)?.sessionLogId
                    ? String((dislikeConfirmTarget as any).sessionLogId)
                    : "";
                  if (!sessionLogId) {
                    message.warning(t("opsmate.missingSessionLog"));
                    return;
                  }
                  if (dislikeReasons.length === 0 && !dislikeOtherReason.trim()) {
                    message.warning(t("opsmate.dislikeReasonRequired"));
                    return;
                  }
                  const flags = (dislikeReasons || [])
                    .map((r) => dislikeReasonToFlag[r] || r)
                    .filter(Boolean);
                  const feedbackFlags = flags.length > 0 ? flags.join(",") : "None";
                  try {
                    await sessionAnswerReport({
                      Entity: {
                        ID: sessionLogId,
                        FeedbackFlags: feedbackFlags,
                        FeedbackDescription: dislikeOtherReason || "",
                      },
                    });
                    await confirmDislike?.();
                    resetDislikeForm();
                  } catch (e) {
                    console.error("sessionAnswerReport failed", e);
                    message.error(t("opsmate.feedbackFailed"));
                  }
                }}
                style={{
                  width: 88,
                  height: 32,
                  background: "#830051",
                  borderRadius: 4,
                  borderColor: "#830051",
                  boxShadow: "none",
                }}
              >
                {t("opsmate.submit")}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
}

/**
 * OpsMate 页面入口
 * Provider 与 Layout 已提升至 OpsMateLayout（路由父层），此处仅渲染页面内容，
 * 以保证跨 /opsmate、/general-source、/study-source 切换时 Provider 不重新挂载。
 */
export default function OpsMate() {
  return (
    <div className="opsmate-page">
      <OpsMateInner />
    </div>
  );
}
